// MorphemeAnalysisCalculator.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const MorphemeAnalysisCalculator = () => {
  const [word, setWord] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setWord(e.target.value);
    setError('');
  };

  const calculateMorphemeAnalysis = () => {
    if (!word) {
      setError('Please enter a word');
      return;
    }

    // Mock morpheme analysis calculation logic
    const root = word.slice(0, 3); // Assuming the first 3 characters as the root
    const prefix = word.startsWith('un') ? 'un-' : '';
    const suffix = word.endsWith('ing') ? '-ing' : '';
    const base = word.replace(prefix, '').replace(suffix, '');

    const analysisResult = {
      root,
      prefix,
      suffix,
      base,
      explanation: `The word "${word}" consists of the root "${root}", prefix "${prefix}", and suffix "${suffix}". The base form is "${base}".`
    };

    setAnalysis(analysisResult);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Morpheme Analysis Calculator | Analyze Word Structure</title>
        <meta name="description" content="Analyze the morpheme structure of words, breaking them down into roots, prefixes, and suffixes. Understand word composition and linguistic patterns." />
        <meta name="keywords" content="morpheme analysis, word structure, linguistic analysis, root words, prefixes, suffixes" />
        <meta property="og:title" content="Morpheme Analysis Calculator | Analyze Word Structure" />
        <meta property="og:description" content="Analyze the morpheme structure of words, breaking them down into roots, prefixes, and suffixes. Understand word composition and linguistic patterns." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/morpheme-analysis-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔍</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Morpheme Analysis Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Analyze the morpheme structure of a word</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator provides a mock morpheme analysis of a given word, breaking it down into its root, prefix, and suffix.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="word">
            Word:
          </label>
          <input
            type="text"
            id="word"
            value={word}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a word"
          />
        </div>

        <button
          onClick={calculateMorphemeAnalysis}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Morphemes
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {analysis && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-4">Morpheme Analysis</h4>
              <div className="space-y-2">
                <p className="flex justify-between">
                  <span>Root</span>
                  <span className="font-medium">{analysis.root}</span>
                </p>
                <p className="flex justify-between">
                  <span>Prefix</span>
                  <span className="font-medium">{analysis.prefix}</span>
                </p>
                <p className="flex justify-between">
                  <span>Suffix</span>
                  <span className="font-medium">{analysis.suffix}</span>
                </p>
                <p className="flex justify-between">
                  <span>Base Form</span>
                  <span className="font-medium">{analysis.base}</span>
                </p>
                <p className="flex justify-between">
                  <span>Explanation</span>
                  <span className="font-medium">{analysis.explanation}</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Morpheme Analysis Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Morpheme Analysis Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Word</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the word in the "Word" field.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Analyze Morphemes</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Analyze Morphemes" button.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the morpheme analysis results.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Full FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the Morpheme Analysis Calculator?</h3>
              <p className="text-gray-700">
                The Morpheme Analysis Calculator is a tool that provides a mock morpheme analysis of a given word, breaking it down into its root, prefix, and suffix.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the calculator work?</h3>
              <p className="text-gray-700">
                The calculator uses a simple algorithm to identify the root, prefix, and suffix of the input word.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Is the analysis accurate?</h3>
              <p className="text-gray-700">
                No, the analysis is a mock representation for demonstration purposes only. It is not based on any scientific or linguistic analysis.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this tool for any words?</h3>
              <p className="text-gray-700">
                Yes, you can use this tool for any words. However, the results are for fun and should not be taken seriously.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Instant Results</h3>
              <p className="text-gray-700">
                Get immediate morpheme analysis for any given word.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">User-Friendly Interface</h3>
              <p className="text-gray-700">
                Easy-to-use interface with clear instructions and intuitive design.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Fun and Engaging</h3>
              <p className="text-gray-700">
                A fun way to see the morpheme structure of a word, perfect for entertainment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MorphemeAnalysisCalculator;